"use client";

/* oxlint-disable jsx-a11y/prefer-tag-over-role, jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions -- The labelled carousel group owns arrow-key selection; nested replay controls keep their own keyboard behavior. */

import * as stylex from "@stylexjs/stylex";
import { type KeyboardEvent, type ReactNode, useState } from "react";
import type { CatalogMediaItem } from "./media-types";
import { styles } from "./media.styles";
import { NamePlate } from "./name-plate";
import { StageContent } from "./media-stage-content";
import { MediaThumbnailStrip } from "./media-thumbnail-strip";

export function MediaCarousel({
  items,
  name,
  uppercase,
  board,
  replayEmbed,
  landscape = false,
}: {
  readonly items: readonly CatalogMediaItem[];
  readonly name: string;
  readonly uppercase: boolean;
  readonly board?: ReactNode;
  readonly replayEmbed?: ReactNode;
  /** Keeps a 16:9 frame at every width and contains odd-aspect images. */
  readonly landscape?: boolean | undefined;
}) {
  const [selection, setSelection] = useState<string | null>(null);
  const [broken, setBroken] = useState<ReadonlySet<string>>(new Set());
  const occurrences = new Map<string, number>();
  const entries = items.map((item) => {
    const descriptor = JSON.stringify([
      item.kind,
      item.src,
      item.alt,
      item.poster ?? null,
    ]);
    const occurrence = occurrences.get(descriptor) ?? 0;
    occurrences.set(descriptor, occurrence + 1);
    return { key: `${descriptor}:${occurrence}`, item };
  });
  const selected = Math.max(
    0,
    entries.findIndex((entry) => entry.key === selection),
  );
  const selectedEntry = entries[selected];

  function onStageKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (items.length < 2 || event.target !== event.currentTarget) return;

    let next: number;

    if (event.key === "ArrowRight") next = (selected + 1) % items.length;
    else if (event.key === "ArrowLeft")
      next = (selected + items.length - 1) % items.length;
    else return;

    event.preventDefault();

    for (const [index, entry] of entries.entries()) {
      if (index === next) setSelection(entry.key);
    }
  }

  if (!selectedEntry) return null;

  const { item, key: selectedKey } = selectedEntry;
  return (
    <div {...stylex.props(styles.carousel)}>
      <div
        data-store-stage={item.kind}
        role="group"
        aria-roledescription="media carousel"
        aria-label={`${name} media`}
        tabIndex={0}
        onKeyDown={onStageKeyDown}
        {...stylex.props(styles.frame, landscape && styles.landscape)}
      >
        {broken.has(selectedKey) ? (
          <NamePlate name={name} uppercase={uppercase} />
        ) : (
          <StageContent
            key={selectedKey}
            item={item}
            board={board}
            replayEmbed={replayEmbed}
            containOdd={landscape}
            onError={() =>
              setBroken((previous) => new Set(previous).add(selectedKey))
            }
          />
        )}
      </div>
      <p aria-live="polite" {...stylex.props(styles.screenReader)}>
        {`Image ${selected + 1} of ${items.length}: ${item.alt}`}
      </p>
      {items.length > 1 ? (
        <MediaThumbnailStrip
          entries={entries}
          selectedKey={selectedKey}
          onSelect={setSelection}
        />
      ) : null}
    </div>
  );
}
